import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { PoolLocations, PoolMaterials } from "../../context/types";
import { ReactComponent as PoolLocationIcon } from "../../assets/pool-location.svg";
import { ReactComponent as MaterialIcon } from "../../assets/material.svg";
import { ReactComponent as PlusIcon } from "../../assets/plus.svg";
import { ReactComponent as PoolRecImg } from "../../assets/pool-rec.svg";
import { ReactComponent as PoolCircleImg } from "../../assets/pool-circle.svg";
import { ReactComponent as PoolKidneyImg } from "../../assets/pool-kidney.svg";
import { ReactComponent as PoolSquareImg } from "../../assets/pool-square.svg";
import { ShapeItemContainer } from "./styles";
import { PoolShape } from "../../constants/pool";
import { valueLabelFormat } from "../../utils/tempSlider";
import useResponsive from "../../hooks/useResponsive";
import { useFormContext } from "react-hook-form";
import { FirstStepFormData } from "../../schemas/validationSchemas";
import { FormSlider, FormDropdown, FormNumberField } from "../common";

export function PoolDetailsForm() {
  const { isSm, isXs } = useResponsive();
  const isMobile = isSm || isXs;
  const { control, watch, setValue } = useFormContext<FirstStepFormData>();

  const shapeValue = watch("shape");
  const coversOptions = ["Yes", "No"];

  const handleChangeShape = (value: PoolShape) => {
    setValue("shape", value);
  };

  
  const adaptedValueLabelFormat = (value: number, unit?: string) => {
    return valueLabelFormat(value, unit || "");
  };

  const spacing = isMobile ? 2 : 4;

  return (
    <Container>
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={isMobile ? "start" : "center"}
        justifyContent={"center"}
      >
        <Typography textAlign={isMobile ? "left" : "center"}>
          Pool Type
        </Typography>
        <Grid
          container
          spacing={spacing}
          mt={spacing}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Grid item lg={4}>
            <FormDropdown
              name="location"
              control={control}
              items={PoolLocations}
              Icon={PoolLocationIcon}
              placeholder="Pool Location"
            />
          </Grid>
          <Grid item lg={4}>
            <FormDropdown
              name="withCover"
              control={control}
              items={coversOptions}
              Icon={PlusIcon}
              placeholder="Cover"
            />
          </Grid>
          <Grid item lg={4}>
            <FormDropdown
              name="poolMaterial"
              control={control}
              items={PoolMaterials}
              Icon={MaterialIcon}
              placeholder="Material"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={4}>
          <Grid item xs={12} sm={12} md={12} lg={4}>
            <FormSlider
              name="requirePoolTemp"
              control={control}
              label="Required Pool Temperature (°C)"
              min={20}
              max={40}
              step={1}
              unit="°C"
              valueLabelFormat={adaptedValueLabelFormat}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={4}>
            <FormSlider
              name="operatingHours"
              control={control}
              label="Operating hours (6-14)"
              min={6}
              max={14}
              step={1}
              unit="hrs"
              valueLabelFormat={adaptedValueLabelFormat}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={4} mt={-2}>
            <Typography id="electricity-cost">Electricity Cost</Typography>
            <Stack flexDirection={"row"} alignItems={"center"}>
              <FormNumberField
                name="electricityCost"
                control={control}
                label=""
                placeholder="0.00"
                sx={{
                  "& input": {
                    fontSize: 32,
                    fontWeight: "bold",
                    width: "300px",
                    textAlign: "center",
                  },
                }}
                variant="standard"
                focused
              />
              <Typography mt={2} ml={-8}>
                $/kw
              </Typography>
            </Stack>
          </Grid>
        </Grid>
        <Typography my={6} mt={10} textAlign={"center"}>
          Pool Shape
        </Typography>

        <Grid
          container
          gap={isMobile ? 4 : 10}
          justifyContent={"center"}
          alignItems={"center"}
          gridTemplateColumns={2}
          gridTemplateRows={2}
        >
          <ShapeItemContainer
            highlighted={shapeValue === PoolShape.SQUARE}
            onClick={() => handleChangeShape(PoolShape.SQUARE)}
          >
            <Box
              width={isMobile ? 130 : 190}
              height={isMobile ? 130 : 190}
              display={"flex"}
              justifyContent={"center"}
              alignItems="center"
              sx={{ background: "#dedede" }}
            >
              <PoolSquareImg />
            </Box>
            Square
          </ShapeItemContainer>
          <ShapeItemContainer
            highlighted={shapeValue === PoolShape.RECTANGULAR}
            onClick={() => handleChangeShape(PoolShape.RECTANGULAR)}
          >
            <Box
              width={isMobile ? 130 : 190}
              height={isMobile ? 130 : 190}
              display={"flex"}
              justifyContent={"center"}
              alignItems="center"
              sx={{ background: "#dedede" }}
            >
              <PoolRecImg />
            </Box>
            Rectangular
          </ShapeItemContainer>
          <ShapeItemContainer
            highlighted={shapeValue === PoolShape.ROUND}
            onClick={() => handleChangeShape(PoolShape.ROUND)}
          >
            <Box
              width={isMobile ? 130 : 190}
              height={isMobile ? 130 : 190}
              display={"flex"}
              justifyContent={"center"}
              alignItems="center"
              sx={{ background: "#dedede" }}
            >
              <PoolCircleImg />
            </Box>
            Circle
          </ShapeItemContainer>
          <ShapeItemContainer
            highlighted={shapeValue === PoolShape.KIDNEY}
            onClick={() => handleChangeShape(PoolShape.KIDNEY)}
          >
            <Box
              width={isMobile ? 130 : 190}
              height={isMobile ? 130 : 190}
              display={"flex"}
              justifyContent={"center"}
              alignItems="center"
              sx={{ background: "#dedede" }}
            >
              <PoolKidneyImg />
            </Box>
            Kidney
          </ShapeItemContainer>
        </Grid>
      </Box>
    </Container>
  );
}
